import { createRider, deleteRider, updateRider } from "../rider/Rider";
import { createOwner, deleteOwner, updateOwner } from "../owner/Owner";
import { createTrainer, deleteTrainer, updateTrainer } from "../trainer/Trainer";
import { BarnPerson, FullRole } from "../../interfaces/Person";
import { CreateAddressInput, CreateContactInput, CreateOwnerInput, CreatePersonalInformationInput, CreateRiderInput, CreateTrainerInput, UpdateAddressInput, UpdateContactInput, UpdateOwnerInput, UpdatePersonalInformationInput, UpdateRiderInput, UpdateTrainerInput } from "../../API";
import moment from "moment";
import { Result } from "../../interfaces/Result";
import { Address, Contact, EventEntry, PersonalInformation, Membership, Owner, Rider, Trainer } from "../../models";
import { formatTwilioNumber } from "../contact/FormatPhoneNumber";
import { createContact, updateContact } from "../contact/Contact";
import { getMembershipsByOwnerId, getMembershipsByPersonId, getMembershipsByRiderId, getMembershipsByTrainerId } from "../membership/Membership";
import { createPersonalInformation, getPersonalInformationByPersonId, updatePersonalInformation } from "../personalInformation/PersonalInformation";
import { createAddress, updateAddress } from "../address/Address";
import { formatShortAddress } from "../address/FormatAddress";


export async function getPersonAddress(barnPerson: BarnPerson): Promise<Address | null> {
    let address: Address | null | undefined = null;
    let foundAddresses: Address[] = [];

    const fullRoles = barnPerson.fullRoles;
    if (fullRoles && fullRoles.length > 0) {
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];
            if (fullRole.role === "rider") {
                let possibleAddress = fullRole.rider?.address;
                if (possibleAddress) {
                    foundAddresses.push(possibleAddress);
                    if (possibleAddress?.city) {
                        address = possibleAddress;
                    }
                }
            }
            else if (fullRole.role === "owner") {
                let possibleAddress = fullRole.owner?.address;
                if (possibleAddress) {
                    foundAddresses.push(possibleAddress);
                    if (possibleAddress?.city) {
                        address = possibleAddress;
                    }
                }
            } else if (fullRole.role === "trainer") {
                let possibleAddress = fullRole.trainer?.address;
                if (possibleAddress) {
                    foundAddresses.push(possibleAddress);
                    if (possibleAddress?.city) {
                        address = possibleAddress;
                    }
                }
            }
        }
    }

    return address;
}

export async function getPersonContact(barnPerson: BarnPerson): Promise<Contact | null> {
    let contact: Contact | null | undefined = null;
    let foundContacts: Contact[] = [];

    const fullRoles = barnPerson.fullRoles;
    if (fullRoles && fullRoles.length > 0) {
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];
            if (fullRole.role === "rider") {
                let possibleContact = fullRole.rider?.contact;
                if (possibleContact) {
                    foundContacts.push(possibleContact);
                    if (possibleContact?.cell) {
                        contact = possibleContact;
                    }
                }
            }
            else if (fullRole.role === "owner") {
                let possibleContact = fullRole.owner?.contact;
                if (possibleContact) {
                    foundContacts.push(possibleContact);
                    if (possibleContact?.cell) {
                        contact = possibleContact;
                    }
                }
            } else if (fullRole.role === "trainer") {
                let possibleContact = fullRole.trainer?.contact;
                if (possibleContact) {
                    foundContacts.push(possibleContact);
                    if (possibleContact?.cell) {
                        contact = possibleContact;
                    }
                }
            }
        }
    }

    return contact;
}

async function getMembershipsForRider(rider: Rider): Promise<Membership[]> {
    let memberships: Membership[] = [];
    const queryResult = await getMembershipsByRiderId(rider.id);
    if (queryResult.isSuccess) {
        memberships = queryResult.result;
    }
    return memberships;
}

async function getMembershipsForOwner(owner: Owner): Promise<Membership[]> {
    let memberships: Membership[] = [];
    const queryResult = await getMembershipsByOwnerId(owner.id);
    if (queryResult.isSuccess) {
        memberships = queryResult.result;
    }
    return memberships;
}

async function getMembershipsForTrainer(trainer: Trainer): Promise<Membership[]> {
    let memberships: Membership[] = [];
    const queryResult = await getMembershipsByTrainerId(trainer.id);
    if (queryResult.isSuccess) {
        memberships = queryResult.result;
    }
    return memberships;
}

export async function getPersonMemberships(barnPerson: BarnPerson): Promise<Membership[]> {
    let memberships: Membership[] = [];

    // First, try to get the memberships for the person attached to the barn person
    const personId = barnPerson.personId;
    if (personId) {
        const queryResult = await getMembershipsByPersonId(personId);
        if (queryResult.isSuccess) {
            memberships = queryResult.result;
        }
    }

    // Next, try to get the memberships for the roles attached to the barn person
    const fullRoles = barnPerson.fullRoles;
    if (fullRoles && fullRoles.length > 0) {
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];
            if (fullRole.role === "rider" && fullRole.rider) {
                const riderMemberships: Membership[] = await getMembershipsForRider(fullRole.rider);
                if (riderMemberships && riderMemberships.length > 0) {
                    riderMemberships.forEach((riderMembership: Membership) => {
                        const index = memberships.findIndex(m => m.id === riderMembership.id);
                        if (index === -1) memberships.push(riderMembership);
                    });
                }
            } else if (fullRole.role === "owner" && fullRole.owner) {
                const ownerMemberships: Membership[] = await getMembershipsForOwner(fullRole.owner);
                if (ownerMemberships && ownerMemberships.length > 0) {
                    ownerMemberships.forEach((ownerMembership: Membership) => {
                        const index = memberships.findIndex(m => m.id === ownerMembership.id);
                        if (index === -1) memberships.push(ownerMembership);
                    });
                }
            } else if (fullRole.role === "trainer" && fullRole.trainer) {
                const trainerMemberships: Membership[] = await getMembershipsForTrainer(fullRole.trainer);
                if (trainerMemberships && trainerMemberships.length > 0) {
                    trainerMemberships.forEach((trainerMembership: Membership) => {
                        const index = memberships.findIndex(m => m.id === trainerMembership.id);
                        if (index === -1) memberships.push(trainerMembership);
                    });
                }
            }
        }
    }
    
    return memberships;
}

export async function savePersonBasicInfo(barnPerson: BarnPerson, name?: string ) {
    let result: Result = {
        isSuccess: true,
        type: "BarnPerson",
        result: barnPerson,
        message: "Successfully updated the barn person"
    };

    // Loop through the full roles and update the basic info for each role
    const fullRoles = barnPerson.fullRoles;
    if (fullRoles && fullRoles.length > 0) {
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];

            if (fullRole.role === "rider" && fullRole.rider) {
                const updateRiderInput: UpdateRiderInput = {
                    id: fullRole.rider.id,
                    name: name
                };
                const updateRiderResult = await updateRider(updateRiderInput);
                if (!updateRiderResult.isSuccess) {
                    result = {isSuccess: false, type: "BarnPerson", result: updateRiderResult.result, message: "Could not update the barn person."};
                }
            } 
            else if (fullRole.role === "owner" && fullRole.owner) {
                const updateOwnerInput: UpdateOwnerInput = {
                    id: fullRole.owner.id,
                    name: name
                };
                const updateOwnerResult = await updateOwner(updateOwnerInput);
                if (!updateOwnerResult.isSuccess) {
                    result = {isSuccess: false, type: "BarnPerson", result: updateOwnerResult.result, message: "Could not update the barn person."};
                }
            }
            else if (fullRole.role === "trainer" && fullRole.trainer) {
                const updateTrainerInput: UpdateTrainerInput = {
                    id: fullRole.trainer.id,
                    name: name
                };
                const updateTrainerResult = await updateTrainer(updateTrainerInput);
                if (!updateTrainerResult.isSuccess) {
                    result = {isSuccess: false, type: "BarnPerson", result: updateTrainerResult.result, message: "Could not update the barn person."};
                }
            }
        }
    }
    return result;
}

export async function savePersonRiderInfo(barnPerson: BarnPerson, birthdate?: string, isProfessional?: boolean ) {
    let result: Result = {
        isSuccess: true,
        type: "BarnPerson",
        result: barnPerson,
        message: "Successfully updated the barn person"
    };

    const fullRoles = barnPerson.fullRoles;
    if (fullRoles && fullRoles.length > 0) {
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];

            if (fullRole.role === "rider" && fullRole.rider) {
                let updateRiderInput: UpdateRiderInput = {
                    id: fullRole.rider.id,
                    isProfessional: isProfessional
                };
                if (birthdate) updateRiderInput.birthdate = moment(birthdate).format("YYYY-MM-DD");
                else updateRiderInput.birthdate = null;
                const updateRiderResult = await updateRider(updateRiderInput);
                if (!updateRiderResult.isSuccess) {
                    result = {isSuccess: false, type: "BarnPerson", result: updateRiderResult.result, message: "Could not update the barn person."};
                }
            }
        }
    }

    // Next, try to get the personal information for the person attached to the barn person
    if (barnPerson.personId) {
        const queryResult = await getPersonalInformationByPersonId(barnPerson.personId);
        if (queryResult.isSuccess) {
            const info: PersonalInformation = queryResult.result;
            let updateInfoInput: UpdatePersonalInformationInput = {
                id: info.id
            };
            if (birthdate) updateInfoInput.dateOfBirth = moment(birthdate).format("YYYY-MM-DD");
            else updateInfoInput.dateOfBirth = null;
            const updateResult = await updatePersonalInformation(updateInfoInput);
            if (!updateResult.isSuccess) {
                result = {isSuccess: false, type: "BarnPerson", result: updateResult.result, message: "Could not update the barn person."};
                return result;
            }
        } else if (birthdate) {
            const createInfoInput: CreatePersonalInformationInput = {
                personId: barnPerson.personId,
                dateOfBirth: moment(birthdate).format("YYYY-MM-DD"),
            };
            const createResult = await createPersonalInformation(createInfoInput);
            if (!createResult.isSuccess) {
                result = {isSuccess: false, type: "BarnPerson", result: createResult.result, message: "Could not update the barn person."};
                return result;
            }
        }
    }

    return result;
}

export async function savePersonContactInfo(barnPerson: BarnPerson, email?: string, phoneNumber?:string, phoneType?: string) {
    let result: Result = {isSuccess: true, type: "Person", result: null, message: "Successfully updated the contact info."};

    let contact: (Contact | null) = await getPersonContact(barnPerson);

    // Check the fullRoles and determine if the person is a rider, owner or trainer
    const fullRoles = barnPerson.fullRoles;
    const isRider = barnPerson.fullRoles?.some(role => role.role === "rider");
    const isOwner = barnPerson.fullRoles?.some(role => role.role === "owner");
    const isTrainer = barnPerson.fullRoles?.some(role => role.role === "trainer");

    if (contact) {
        // Update the contact
        let contactInput: UpdateContactInput = {
            id: contact.id,
            name: barnPerson.name || ""            
        };
        if (phoneNumber) {
            const formattedPhoneNumber = formatTwilioNumber(phoneNumber);
            if (phoneType === "cell") contactInput["cell"] = formattedPhoneNumber;
            if (phoneType === "home") contactInput["home"] = formattedPhoneNumber;
            if (phoneType === "work") contactInput["work"] = formattedPhoneNumber;
        } else {
            if (phoneType === "cell") contactInput["cell"] = null;
            if (phoneType === "home") contactInput["home"] = null;
            if (phoneType === "work") contactInput["work"] = null;
        }
        if (isRider || isOwner) {
            contactInput["personalEmail"] = email;
        }
        if (isTrainer) {
            contactInput["workEmail"] = email;
        }
        const updateContactResult = await updateContact(contactInput);
        if (updateContactResult.isSuccess) {
            contact = updateContactResult.result;
        }
    } else {
        // Create the contact
        let contactInput: CreateContactInput = {
            name: barnPerson.name
        };
        if (phoneNumber) {
            const formattedPhoneNumber = formatTwilioNumber(phoneNumber);
            if (phoneType === "cell") contactInput["cell"] = formattedPhoneNumber;
            if (phoneType === "home") contactInput["home"] = formattedPhoneNumber;
            if (phoneType === "work") contactInput["work"] = formattedPhoneNumber;
        }
        if (isRider || isOwner) {
            contactInput["personalEmail"] = email;
        }
        if (isTrainer) {
            contactInput["workEmail"] = email;
        }
        const createContactResult = await createContact(contactInput);
        if (createContactResult.isSuccess) {
            contact = createContactResult.result;
        }
    }

    if (contact) {
        // Now connect the contact to any rider, owner or trainer objects
        if (fullRoles && fullRoles.length > 0) {
            for (let i = 0; i < fullRoles.length; i++) {
                const fullRole = fullRoles[i];
                if (fullRole.role === "rider" && fullRole.rider) {
                    const updateRiderInput: UpdateRiderInput = {
                        id: fullRole.rider.id,
                        riderContactId: contact?.id
                    };
                    const updateRiderResult = await updateRider(updateRiderInput);
                    if (!updateRiderResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update rider contact information"};
                    }
                } else if (fullRole.role === "owner" && fullRole.owner) {
                    const updateOwnerInput: UpdateOwnerInput = {
                        id: fullRole.owner.id,
                        ownerContactId: contact?.id
                    };
                    const updateOwnerResult = await updateOwner(updateOwnerInput);
                    if (!updateOwnerResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update owner contact information"};
                    }
                } else if (fullRole.role === "trainer" && fullRole.trainer) {
                    const updateTrainerInput: UpdateTrainerInput = {
                        id: fullRole.trainer.id,
                        trainerContactId: contact?.id
                    };
                    const updateTrainerResult = await updateTrainer(updateTrainerInput);
                    if (!updateTrainerResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update trainer contact information"};
                    }
                }
            }
        }
    } else {
        result = {isSuccess: false, type: "Person", result: null, message: "No contact information to update."};
    }
    return result;
}

export async function savePersonAddress(barnPerson: BarnPerson, streetAddress1?: string, streetAddress2?: string, city?: string, provState?: string, zip?: string, country?: string) {
    let result: Result = {isSuccess: true, type: "Person", result: null, message: "Successfully updated the address info."};

    let address: (Address | null) = await getPersonAddress(barnPerson);

    if (address) {
        // Update the address
        let addressInput: UpdateAddressInput = {
            id: address.id,
            name: barnPerson.name || "",
            streetAddress1: streetAddress1,
            streetAddress2: streetAddress2,
            city: city,
            provState: provState,
            zip: zip,
            country: country               
        };
        const updateAddressResult = await updateAddress(addressInput);
        if (updateAddressResult.isSuccess) {
            address = updateAddressResult.result;
        }
    } else {
        // Create the address
        let addressInput: CreateAddressInput = {
            name: barnPerson.name || "",
            streetAddress1: streetAddress1,
            streetAddress2: streetAddress2,
            city: city,
            provState: provState,
            zip: zip,
            country: country,
            notes: "Created on the Barn Person Page"
        };
        const createAddressResult = await createAddress(addressInput);
        if (createAddressResult.isSuccess) {
            address = createAddressResult.result;
        }
    }

    if (address) {
        const fullRoles = barnPerson.fullRoles;
        // Now connect the address to any rider, owner or trainer objects
        if (fullRoles && fullRoles.length > 0) {
            for (let i = 0; i < fullRoles.length; i++) {
                const fullRole = fullRoles[i];
                if (fullRole.role === "rider" && fullRole.rider) {
                    const updateRiderInput: UpdateRiderInput = {
                        id: fullRole.rider.id,
                        addressId: address.id || null,
                        location: formatShortAddress(address)
                    };
                    const updateRiderResult = await updateRider(updateRiderInput);
                    if (!updateRiderResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update rider address information"};
                    }
                } else if (fullRole.role === "owner" && fullRole.owner) {
                    const updateOwnerInput: UpdateOwnerInput = {
                        id: fullRole.owner.id,
                        addressId: address.id,
                        location: formatShortAddress(address)
                    };
                    const updateOwnerResult = await updateOwner(updateOwnerInput);
                    if (!updateOwnerResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update owner address information"};
                    }
                } else if (fullRole.role === "trainer" && fullRole.trainer) {
                    const updateTrainerInput: UpdateTrainerInput = {
                        id: fullRole.trainer.id,
                        addressId: address.id,
                        location: formatShortAddress(address)
                    };
                    const updateTrainerResult = await updateTrainer(updateTrainerInput);
                    if (!updateTrainerResult.isSuccess) {
                        result = {isSuccess: false, type: "Person", result: null, message: "Could not update trainer address information"};
                    }
                }
            }
        }
    } else {
        result = {isSuccess: false, type: "Person", result: null, message: "No address information to update."};
    }
    return result;
}

export function formatBarnPersonFromEventEntry(entry: EventEntry, isSecondRider?: boolean): BarnPerson {
    const fullRoles: FullRole[] = [];
    if (isSecondRider) {
            if (entry.secondRider) {
                const riderRole: FullRole = {
                    id: entry.secondRider.id,
                    role: "rider",
                    rider: entry.secondRider
                };
                fullRoles.push(riderRole);
            }
    } else {
        if (entry.rider) {
            const riderRole: FullRole = {
                id: entry.rider.id,
                role: "rider",
                rider: entry.rider
            };
            fullRoles.push(riderRole);
        }
    }
    if (entry.owner) {
        const ownerRole: FullRole = {
            id: entry.owner.id,
            role: "owner",
            owner: entry.owner
        };
        fullRoles.push(ownerRole);
    }
    if (entry.trainer) {
        const trainerRole: FullRole = {
            id: entry.trainer.id,
            role: "trainer",
            trainer: entry.trainer
        };
        fullRoles.push(trainerRole);
    }
    const barnPerson: BarnPerson = {
        barnId: entry.barnId || "",
        name: entry.rider?.name || entry.riderName || "",
        personId: entry.rider?.personId || "",
        fullRoles: fullRoles,
        createdBy: "",
        creatorName: "",
    };
    return barnPerson;
}

export async function updatePersonRoles(barnPerson: BarnPerson, isRider?: boolean, isOwner?: boolean, isTrainer?: boolean, barnId?: string): Promise<Result> {
    let result: Result = {isSuccess: true, type: "Person", result: null, message: "Updated the roles."};

    const fullRoles = barnPerson.fullRoles;

    let personId: string = barnPerson.personId || "";
    let currentBarnId: string = barnPerson.barnId || barnId || "";
    if ((!personId || personId === "") && (fullRoles && fullRoles.length > 0)) { 
        for (let i = 0; i < fullRoles.length; i++) {
            const fullRole = fullRoles[i];
            if (fullRole.role === "rider" && fullRole.rider && fullRole.rider.personId) {
                personId = fullRole.rider.personId || "";
                currentBarnId = fullRole.rider.barnId || "";
                break;
            } else if (fullRole.role === "owner" && fullRole.owner && fullRole.owner.personId) {
                personId = fullRole.owner.personId || "";
                currentBarnId = fullRole.owner.barnId || "";
                break;
            } else if (fullRole.role === "trainer" && fullRole.trainer && fullRole.trainer.personId) {
                personId = fullRole.trainer.personId || "";
                currentBarnId = fullRole.trainer.barnId || "";
                break;
            }
        }
    }

    if (fullRoles && fullRoles.some(fr => fr.role === "rider")) {
        if (isRider) {
            // Already has the rider
        } else {
            // Remove the rider
            const riderId = fullRoles.find(fr => fr.role === "rider")?.id;
            if (riderId) {
                await deleteRider({id: riderId});
            } else {
                const message = result.message + " Could not update the rider role. No rider ID found.";
                result = {isSuccess: false, type: "Person", result: null, message: message};
            }
        }
    } else {
        if (isRider) {
            // Needs a new rider
            const createRiderInput: CreateRiderInput = {
                name: barnPerson.name,
                location: barnPerson.location,
                personId: personId || "",
                barnId: currentBarnId || null,
                createdBy: barnPerson.createdBy || null,
                createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")             
            };
            const createRiderResult = await createRider(createRiderInput);
            if (!createRiderResult.isSuccess) {
                const message = result.message + " Could not update the rider role. " + createRiderResult.message;
                result = {isSuccess: false, type: "Person", result: createRiderResult.result, message: message};
            }
        } else {
            // Do nothing
        }
    }

    if (fullRoles && fullRoles.some(fr => fr.role === "owner")) {
        if (isOwner) {
            // Already has the owner
        } else {
            // Remove the owner
            const ownerId = fullRoles.find(fr => fr.role === "owner")?.id;
            if (ownerId) {
                await deleteOwner({id: ownerId});
            } else {
                const message = result.message + " Could not update the owner role. No owner ID found.";
                result = {isSuccess: false, type: "Person", result: null, message: message};
            }
        }
    } else {
        if (isOwner) {
            // Needs a new owner
            const createOwnerInput: CreateOwnerInput = {
                name: barnPerson.name,
                location: barnPerson.location,
                personId: personId || "",
                barnId: currentBarnId || null,
                createdBy: barnPerson.createdBy || null,
                createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")             
            };
            const createOwnerResult = await createOwner(createOwnerInput);
            if (!createOwnerResult.isSuccess) {
                const message = result.message + " Could not update the rider role. " + createOwnerResult.message;
                result = {isSuccess: false, type: "Person", result: createOwnerResult.result, message: message};
            }
        } else {
            // Do nothing
        }
    }

    if (fullRoles && fullRoles.some(fr => fr.role === "trainer")) {
        if (isTrainer) {
            // Already has the trainer
        } else {
            // Remove the trainer
            const trainerId = fullRoles.find(fr => fr.role === "trainer")?.id;
            if (trainerId) {
                await deleteTrainer({id: trainerId});
            } else {
                const message = result.message + " Could not update the trainer role. No trainer ID found.";
                result = {isSuccess: false, type: "Person", result: null, message: message};
            }
        }
    } else {
        if (isTrainer) {
            // Needs a new trainer
            const createTrainerInput: CreateTrainerInput = {
                name: barnPerson.name,
                location: barnPerson.location,
                personId: personId || "",
                barnId: currentBarnId || null,
                createdBy: barnPerson.createdBy || null,
                createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")             
            };
            const createTrainerResult = await createTrainer(createTrainerInput);
            if (!createTrainerResult.isSuccess) {
                const message = result.message + " Could not update the trainer role. " + createTrainerResult.message;
                result = {isSuccess: false, type: "Person", result: createTrainerResult.result, message: message};
            }
        } else {
            // Do nothing
        }
    }

    return result;
}